import { useContext, useState, useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ServiceContext } from "../../../contexts/ServiceContext";
import { ReactComponent as DescriptionIcon } from "../../../icons/description-settings.svg";
import { ReactComponent as EmailIcon } from "../../../icons/email-settings.svg";
import { AppAlert } from "../../../utils/AppAlert";
import { ApiError } from "../../../services/ApiError";
import PhoneInput from 'react-phone-number-input';
import Select from "react-select";
import config from "../../../config";
import 'react-phone-number-input/style.css';
import './user.css';

function User () {
  const { infrastructureService } = useContext(ServiceContext);
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const userRole = localStorage.getItem("user_role");
  const Global_Admin_Role = "Global Admin";
  
  const { userId } = useParams();

  const navigate = useNavigate();

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#f5f5f5",
    borderColor: state.isFocused ? "#c7c4c4ff" : "#ccc",
    boxShadow: state.isFocused ? "0 0 0 2px #b1aeaeff" : "none",
    "&:hover": { borderColor: "#3f3f3fff" },
    borderRadius: "8px",
    padding: "0 4px",       
    minHeight: "45px",      
    width: "100%"
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "45px",         
    padding: "0 6px",
    alignItems: "center",  
    justifyContent: "center",  
    paddingBottom: "10px" 
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    height: "100%"
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#525050"
      : state.isFocused
      ? "#ebe8e8ff"
      : "white",
    color: state.isSelected ? "white" : "#525050",
    padding: "5px 10px",   
    cursor: "pointer",
    fontWeight: state.isSelected ? "bold" : "normal",
    transition: "background-color 0.1s ease",
    ":active": {
      backgroundColor: "#525050",
      color: "white"
    }
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#888",
    fontStyle: "italic",
    textAlign: "center",
    margin: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#222",
    margin: 0,
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? "#3f3f3fff" : "#999",
    "&:hover": { color: "#3f3f3fff" }
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "4px",
    boxShadow: "0 4px 12px #525050"
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0
  })
};
  
const roleOptions = useMemo(() => {
  if (userRole === Global_Admin_Role) {
    return [
      { value: "global_admin", label: "Global Admin" },
      { value: "company_admin", label: "Company Admin" },
    ];
  } else {
    return [
      { value: "company_admin", label: "Company Admin" },
    ];
  }
}, [userRole]);

  const handleChange = (selectedOption) => {
    setCompanyId(selectedOption?.value || null);
  };

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      let getCompanies = {
        filter: "",
        showBlocked: false,
        fromIndex: 0,
        itemsCount: config.uiSettings.itemsPerPage
      };
      const data = await infrastructureService.get("Companies", getCompanies); 
      setCompanies(data.items);
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setLoading(false);
    }
  }, [infrastructureService]);
    
  useEffect(() => {
      if(userId !== "new"){
        const fetchUser = async () => {
          let user = {
            userId: userId
          };
          try {
            const data = await infrastructureService.get("User", user); 
            setFirstName(data.firstName);
            setLastName(data.lastName);
            setCompanyId(data.companyId);
            setCompanyId(data.companyId);
            setEmail(data.email);
            setPhoneNumber(data.phoneNumber);
            const firstRole = data.roles?.find((c) => c);
            setRole(roleOptions.find((opt) => opt.label === firstRole));
          } catch (error) {
            console.error("Error fetching user:", error);
          } finally {
            setLoading(false);
          }
        };
        
        fetchUser();
      }else{
        setLoading(false);
      }
      loadCompanies();
  }, [infrastructureService, userId, loadCompanies, roleOptions]);
  
  function validateUser({companyId, email, firstName, lastName}) {
    if (!firstName || firstName.trim() === "") {
      setErrorMessage("First name is required");
      return false;
    }
    if (!lastName || lastName.trim() === "") {
      setErrorMessage("Last name is required");
      return false;
    } 
    if (userId === "new" && (!email || email.trim() === "")) {
      setErrorMessage("Email is required");
      return false;
    }
    if (userId === "new" && (!companyId || companyId === 0)) {
      setErrorMessage("Company is required");
      return false;
    } 
    if (!role) {
      setErrorMessage("Role is required");
      return false;
    }
    return true;
  }
    
  async function handleSaveClick() {
      setErrorMessage("");
      if(userId === "new"){
        let effectiveCompanyId = companyId;
    
        if(userRole !== Global_Admin_Role && companies?.length > 0) {
           effectiveCompanyId = companies[0].id;
           setCompanyId(effectiveCompanyId);   
        }
        let createUser = {
          firstName: firstName,
          lastName: lastName,
          companyId: effectiveCompanyId,
          email: email,
          phoneNumber: phoneNumber === "" ? null : phoneNumber,
          roles: [role.label]
        };
        try {
          setLoadingSave(true); 
          if (!validateUser(createUser)) return;
          await infrastructureService.post("User", createUser);
          setIsOpen(true);
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.errors) {
              setErrorMessage(Object.values(err.errors).flat().join("\n"));
            } else {
              setErrorMessage(err.message);
            }
          } else {
            setErrorMessage(err.message || "Unexpected error");
          }
        }
        finally{
          setLoadingSave(false);
        }
      }else{
        let updateUser = {
          id: userId,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: phoneNumber,
          roles: [role.label]
        };
        try {
          setLoadingSave(true); 
          if (!validateUser(updateUser)) return;
          await infrastructureService.put("User", updateUser);
          navigate(-1);
          AppAlert.success("Success! Changes have been applied.");
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.errors) {
              setErrorMessage(Object.values(err.errors).flat().join("\n"));
            } else {
              setErrorMessage(err.message);
            }
          } else {
            setErrorMessage(err.message || "Unexpected error");
          }
        }
        finally{
          setLoadingSave(false);
        }
      }
  } 

  function handleCancelClick() {
    navigate(-1);
  }

  function okClick() {
    navigate(-1);
    AppAlert.success("The user was added successfully");
  } 
  
  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="user-settings">
        {!isOpen && (<div className="container">
          <div className="card">
            <div className="text">User Settings</div>
          </div>
          <div className="inputs">
            <div className="input">
              <DescriptionIcon/>
              <input placeholder="First name" onChange={(e) => setFirstName(e.target.value)} value={firstName}/>
            </div>
            <div className="input">
              <DescriptionIcon/>
              <input placeholder="Last name" onChange={(e) => setLastName(e.target.value)} value={lastName}/>
            </div>
            <div className="input">
              <EmailIcon/>
              <input type="email" placeholder="Email" onChange={(e) => setEmail(e.target.value)} value={email} disabled={userId !== "new"}/>
            </div>
            <div className="input">
                <div style={{paddingLeft: "30px"}}>
                    <PhoneInput value={phoneNumber} onChange={setPhoneNumber} placeholder="Phone number " defaultCountry="US" enableSearch={true}/>
                </div>
            </div>
            {userRole !== undefined && userRole === Global_Admin_Role && (<div className="input settings-select">
                <div className="select-company-u">
                   <Select options={companies.map((c) => ({ value: c.id, label: c.name}))} isSearchable={true} placeholder="Please select a company" styles={customStyles} 
                           value={companies.map((c) => ({ value: c.id, label: c.name })).find((opt) => opt.value === companyId) || null} onChange={handleChange} isDisabled={userId !== "new"}/>
                </div>
            </div>)}
            <div className="input settings-select">
                <div className="select-company-u">
                   <Select isSearchable={true} placeholder="Please select a role" styles={customStyles} options={roleOptions} value={role} 
                           onChange={(selectedOption) => setRole(selectedOption)}/>
                </div>
            </div>
            {errorMessage && (
            <div className="error input">
              <span style={{paddingLeft: "10px"}}>{errorMessage}</span>
            </div>
           )}
           <div className="submit-container">
            <div className="cancel" onClick={handleCancelClick}>Cancel</div>
            {!loadingSave && (<div className="submit" onClick={handleSaveClick}>Save</div>)}
            {loadingSave && (<div className="submit">Loading...</div>)}
           </div>
        </div>
        </div>)}
         {isOpen && (
          <div style={{position: "fixed",top: 0,left: 0,right: 0,bottom: 0,display: "flex",justifyContent: "center",alignItems: "center",zIndex: 1000,}}> 
            <div style={{backgroundColor: "white",borderRadius: "8px",width: "300px",boxShadow: "0 5px 15px rgba(0,0,0,0.3)",}}>
              <div style={{background:"rgb(236, 236, 236)"}}>
                <h2 style={{ margin:"0px",padding:"13px 0px" }}>Success!</h2>
              </div>
              <div style={{border: "solid rgb(236, 236, 236) 1px", padding:"10px", textAlign: "start"}}>
                <p style={{ marginBottom: "20px" }}>An email has been sent to {email} inviting {firstName} {lastName} to join {companies.find((opt) => opt.value === companyId)}.<br/><br/>
                                                    They'll be asked to set a password that will give them access to the SentryTI dashboard.</p>
                <div className="submit-container" style={{padding:"0px 10px", margin: "20px 0px"}}>
                  <div className="cancel" style={{ height: "30px",borderRadius:"8px",fontSize:"16px", visibility: "hidden" }}></div>
                  <div className="submit" style={{ height: "30px",borderRadius:"8px",fontSize:"16px" }} onClick={() => okClick()}>Ok</div>
                </div>
              </div>
            </div>
         </div>
      )}
      </div>
    </div>
  )

}

export default User;